import { useEffect, useRef, useState, type FC } from 'react';
import { LuX, LuPlus, LuInfo, LuChevronLeft, LuArrowRight } from 'react-icons/lu';
import type { ConjuntoTrilho, ModeloTarefa } from '../services/ModeloTarefaService';
import { TRILHOS_PRONTOS, type TrilhoPronto } from '../data/trilhosProntos';
import { COR_ETAPA, etapasComTarefa, descreverAcao } from './TrilhoCanvas';
import { nomeEtapa } from '../data/etapas';
import './NovoAgenteModal.css';
import './NovoTrilhoModal.css';

/** A chave do cartão "Em branco" — nenhum pronto usa este nome. */
export const TRILHO_EM_BRANCO = '__branco';

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/* O prazo do cartão sai das etapas, não do `destaque`: dois dos quatro prontos
   não têm destaque, e a fileira do chip ficaria vazia neles. */
const faixaDePrazo = (etapas: ModeloTarefa[]) => {
    const prazos = etapas.filter(e => e.ativo).map(e => e.prazoDiasUteis);
    if (!prazos.length) return 'sem prazo';
    const min = Math.min(...prazos), max = Math.max(...prazos);
    if (min === max) return min === 1 ? '1 dia útil' : `${min} dias úteis`;
    return `${min}–${max} dias úteis`;
};

const contarDesfechos = (t: TrilhoPronto) => t.etapas.reduce((s, e) => s + e.desfechos.length, 0);

/* Sugestão de nome que não bate com um trilho da empresa: usar o mesmo pronto
   duas vezes esbarraria na regra de nome único e a pessoa só saberia no clique. */
const nomeLivre = (base: string, existentes: string[]) => {
    const usados = new Set(existentes.map(n => n.trim().toLowerCase()));
    if (!usados.has(base.toLowerCase())) return base;
    let i = 2;
    while (usados.has(`${base} ${i}`.toLowerCase())) i++;
    return `${base} ${i}`;
};

interface Props {
    aberta: boolean;
    /** Abrir já com um pronto escolhido (o "Usar" do cartão) cai direto no passo 2. */
    inicial?: string | null;
    /** Os trilhos da empresa — para sugerir nome livre e barrar o repetido. */
    existentes: ConjuntoTrilho[];
    criando: boolean;
    onFechar: () => void;
    /** `chave` é a do pronto, ou null para o trilho em branco. */
    onCriar: (nome: string, chave: string | null) => void;
}

/**
 * NOVO TRILHO — a mesma galeria em dois passos do "Novo agente"
 * (NovoAgenteModal, /chat-rules), pedida por ele em 27/09 no lugar do diálogo
 * "Nome + Começar de".
 *
 * A casca é a do agente — as classes `na-` vêm de NovoAgenteModal.css de
 * propósito, para as duas modais não divergirem. O que é só do trilho (as três
 * etapas do passo 2) mora em NovoTrilhoModal.css, prefixo `nt-`.
 *
 * O passo 2 é diferente do agente porque o conteúdo é: aqui só falta o nome, e
 * o que vem pronto são as três etapas que criam tarefa. Elas ficam lado a lado,
 * na ordem do quadro, em vez de uma coluna à esquerda com um campo solto à
 * direita — que deixaria a coluna do nome quase toda em branco.
 *
 * Sem hover — regra do produto. Seleção é borda + anel; foco tem marca própria.
 */
const NovoTrilhoModal: FC<Props> = ({ aberta, inicial, existentes, criando, onFechar, onCriar }) => {
    const [passo, setPasso] = useState<1 | 2>(1);
    const [chave, setChave] = useState<string | null>(null);
    const [nome, setNome] = useState('');
    const [tentouCriar, setTentouCriar] = useState(false);

    const refNome = useRef<HTMLInputElement | null>(null);
    const refPrimeiroCartao = useRef<HTMLButtonElement | null>(null);

    const nomesExistentes = existentes.map(c => c.nome);
    const sugestaoDe = (k: string | null) => {
        const t = TRILHOS_PRONTOS.find(p => p.chave === k);
        return t ? nomeLivre(t.nome, nomesExistentes) : '';
    };

    const pronto = TRILHOS_PRONTOS.find(t => t.chave === chave) ?? null;
    const emBranco = chave === TRILHO_EM_BRANCO;

    useEffect(() => {
        if (!aberta) return;
        const k = inicial ?? null;
        setChave(k);
        setNome(sugestaoDe(k));
        setTentouCriar(false);
        setPasso(k ? 2 : 1);
        if (!k) requestAnimationFrame(() => refPrimeiroCartao.current?.focus());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberta, inicial]);

    /* Esc no passo 1 fecha; no passo 2 volta, sem perder o nome digitado. */
    useEffect(() => {
        if (!aberta) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || criando) return;
            if (passo === 2) setPasso(1);
            else onFechar();
        };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [aberta, passo, criando, onFechar]);

    useEffect(() => {
        if (aberta && passo === 2) requestAnimationFrame(() => refNome.current?.select());
    }, [aberta, passo]);

    if (!aberta) return null;

    const escolher = (k: string) => {
        setChave(k);
        /* Outro modelo, outra tentativa: o "falta nome" da anterior não vale aqui. */
        setTentouCriar(false);
        /* Trocar de modelo não apaga um nome digitado à mão: só substitui
           enquanto o nome ainda for a sugestão de algum modelo (ou vazio). */
        setNome(atual => {
            const eraSugestao = !atual.trim() || TRILHOS_PRONTOS.some(t => sugestaoDe(t.chave) === atual);
            return eraSugestao ? sugestaoDe(k) : atual;
        });
    };

    const texto = nome.trim();
    const repetido = !!texto && nomesExistentes.some(n => n.trim().toLowerCase() === texto.toLowerCase());
    const vazio = tentouCriar && !texto;

    const criar = () => {
        setTentouCriar(true);
        if (!chave || !texto || repetido) { refNome.current?.focus(); return; }
        onCriar(texto, emBranco ? null : chave);
    };

    /* As três etapas do quadro, na ordem do funil. No branco elas aparecem
       vazias — é o que a pessoa vai encontrar ao abrir o quadro. */
    const etapas = etapasComTarefa().map(s => ({
        statusId: s,
        modelo: pronto?.etapas.find(e => e.statusId === s) ?? null,
    }));

    return (
        <div className="na-overlay" onClick={() => { if (passo === 1 && !criando) onFechar(); }}>
            <div
                className="na nt"
                role="dialog"
                aria-modal="true"
                aria-label={passo === 1 ? 'Novo trilho: escolher um modelo' : `Novo trilho: ${pronto?.nome ?? 'em branco'}`}
                onClick={e => e.stopPropagation()}
            >
                <header className="na-head">
                    <div className="na-head-txt">
                        <h2 className="na-tit">{passo === 1 ? 'Novo trilho' : (pronto?.nome ?? 'Trilho em branco')}</h2>
                        <p className="na-sub">
                            {passo === 1
                                ? 'Escolha um ponto de partida. Tudo continua editável no quadro.'
                                : emBranco
                                    ? 'Dê um nome. As etapas você monta no quadro.'
                                    : 'Confira o que vem pronto e dê um nome ao trilho.'}
                        </p>
                    </div>
                    <button type="button" className="na-x" onClick={onFechar} aria-label="Fechar" disabled={criando}>
                        <LuX size={16} />
                    </button>
                </header>

                {passo === 1 ? (
                    <div className="na-corpo">
                        <div className="na-gal" role="radiogroup" aria-label="Modelos de trilho">
                            {TRILHOS_PRONTOS.map((t, i) => {
                                const escolhido = chave === t.chave;
                                return (
                                    <button
                                        key={t.chave}
                                        type="button"
                                        role="radio"
                                        aria-checked={escolhido}
                                        ref={i === 0 ? refPrimeiroCartao : undefined}
                                        className={`na-card ${escolhido ? 'is-on' : ''}`}
                                        onClick={() => escolher(t.chave)}
                                        onDoubleClick={() => { escolher(t.chave); setPasso(2); }}
                                        onKeyDown={e => { if (e.key === 'Enter') { escolher(t.chave); setPasso(2); } }}
                                    >
                                        <span className="na-card-tit">{t.icone} {t.nome}</span>
                                        <span className="na-card-txt">{t.descricao}</span>
                                        {/* Duas fileiras fixas, como no cartão do agente: todo
                                            cartão fica com a mesma altura. */}
                                        <span className="na-card-anat">
                                            <span className="na-card-chips">
                                                <span className="na-chip is-tom">{faixaDePrazo(t.etapas)}</span>
                                            </span>
                                            <span className="na-card-chips">
                                                <span className="na-chip"><b>{t.etapas.filter(e => e.ativo).length}</b> etapas</span>
                                                <span className="na-chip"><b>{contarDesfechos(t)}</b> desfechos</span>
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}

                            {/* O branco é escolha como as outras, e não um atalho que
                                fecha a modal: trilho em branco também precisa de nome. */}
                            <button
                                type="button"
                                role="radio"
                                aria-checked={emBranco}
                                className={`na-card is-zero ${emBranco ? 'is-on' : ''}`}
                                onClick={() => escolher(TRILHO_EM_BRANCO)}
                                onDoubleClick={() => { escolher(TRILHO_EM_BRANCO); setPasso(2); }}
                                onKeyDown={e => { if (e.key === 'Enter') { escolher(TRILHO_EM_BRANCO); setPasso(2); } }}
                            >
                                <span className="na-card-tit"><LuPlus size={16} /> Em branco</span>
                                <span className="na-card-txt">
                                    As três etapas vazias. Para quem já sabe qual tarefa cada etapa deve criar.
                                </span>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="na-corpo">
                        <div className="na-campo na-w-nome">
                            <label className="na-lab" htmlFor="nt-nome">Nome do trilho</label>
                            <input
                                id="nt-nome"
                                ref={refNome}
                                type="text"
                                maxLength={80}
                                className={`na-inp ${vazio || repetido ? 'is-erro' : ''}`}
                                placeholder="Ex.: Venda consultiva"
                                value={nome}
                                onChange={e => setNome(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') criar(); }}
                                disabled={criando}
                            />
                            <span className={`na-hint ${vazio || repetido ? 'is-erro' : ''}`}>
                                {vazio
                                    ? 'O trilho precisa de um nome.'
                                    : repetido
                                        ? 'Já existe um trilho com esse nome.'
                                        : 'É o nome que aparece na galeria.'}
                            </span>
                        </div>

                        <span className="na-rot">{emBranco ? 'As etapas do quadro' : 'O que vem pronto'}</span>
                        <div className="nt-etapas">
                            {etapas.map(({ statusId, modelo }) => {
                                const desligada = !!modelo && !modelo.ativo;
                                return (
                                    <div key={statusId} className={`nt-etapa ${modelo ? '' : 'is-vazia'}`}>
                                        <div className="nt-etapa-top">
                                            <i className="nt-dot" style={{ backgroundColor: COR_ETAPA[statusId] }} aria-hidden="true" />
                                            <span className="nt-etapa-nm">{nomeEtapa(statusId)}</span>
                                            {desligada && <span className="na-chip">desligada</span>}
                                        </div>
                                        {modelo ? (
                                            <>
                                                <span className="nt-etapa-tit">{modelo.titulo}</span>
                                                <span className="nt-etapa-m">
                                                    {modelo.tipoTarefaNome ?? 'Tarefa'} · {plural(modelo.prazoDiasUteis, 'dia útil', 'dias úteis')}
                                                </span>
                                                <ul className="nt-desf">
                                                    {modelo.desfechos.map(d => (
                                                        <li key={d.ordem}>
                                                            <span className="nt-desf-r">{d.rotulo}</span>
                                                            <span className="nt-desf-a">{descreverAcao(d)}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        ) : (
                                            <span className="nt-etapa-m">Sem tarefa. Você define no quadro.</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <p className="na-aviso nt-aviso">
                            <LuInfo size={13} />
                            <span>
                                O trilho nasce <b>inativo</b> — o que está rodando não muda. Ajuste no quadro e ative quando quiser.
                            </span>
                        </p>
                    </div>
                )}

                <footer className="na-pe">
                    <span className="na-passo">
                        {passo === 1 ? `Passo 1 de 2 · ${TRILHOS_PRONTOS.length} modelos` : 'Passo 2 de 2'}
                    </span>
                    {passo === 1 ? (
                        <>
                            <button type="button" className="na-btn" onClick={onFechar}>Cancelar</button>
                            <button type="button" className="na-btn is-primary" disabled={!chave} onClick={() => setPasso(2)}>
                                Continuar <LuArrowRight size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="na-btn" onClick={() => setPasso(1)} disabled={criando}>
                                <LuChevronLeft size={14} /> Voltar
                            </button>
                            <button type="button" className="na-btn is-primary" onClick={criar} disabled={criando}>
                                {criando ? 'Criando…' : 'Criar e abrir'}
                            </button>
                        </>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default NovoTrilhoModal;
